import React, { useEffect, useState } from "react";
import { Send, Smartphone, AlertTriangle, ExternalLink, Languages } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody, DialogFooter } from "@/components/ui/dialog";

const LANGS = [["fr", "Français"], ["en", "English"], ["es", "Español"], ["pt", "Português"]];
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }) : "");

// One-click "install the app" campaign → every web-only member, each in HIS language
// (bell + web push → /get-app).
export const AppCampaignDialog = ({ open, onOpenChange, onSent }) => {
  const [status, setStatus] = useState(null);
  const [texts, setTexts] = useState({});
  const [lang, setLang] = useState("fr");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/admin/users/app-campaign").then(({ data }) => { setStatus(data); setTexts(data.texts || {}); }).catch(() => setStatus(null));
  }, [open]);

  const setField = (field, value) => setTexts((prev) => ({ ...prev, [lang]: { ...(prev[lang] || {}), [field]: value } }));

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users/app-campaign", { texts });
      toast.success(`Campagne envoyée à ${data.sent} membre(s)`);
      onSent?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi impossible");
    } finally { setBusy(false); }
  };

  const missingIos = status && !status.store_ios_url;
  const current = texts[lang] || { title: "", body: "" };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="sheet" data-testid="app-campaign-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Smartphone size={18} style={{ color: "var(--yalola-primary)" }} /> Campagne « Installe l'app »</DialogTitle>
          <DialogDescription>Notification (cloche + navigateur) envoyée aux membres qui n'utilisent que le web, chacun dans sa langue, avec un lien vers la page /get-app (Google Play / App Store).</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="rounded-xl p-3 text-sm" style={{ background: "var(--yalola-surface-alt)" }} data-testid="app-campaign-targets">
            {status ? (
              <>
                <div><b>{status.targets}</b> membre(s) seront notifiés <span style={{ color: "var(--yalola-text-muted)" }}>({status.web_only} web seulement, ceux notifiés depuis moins de 7 jours sont exclus)</span></div>
                {status.targets > 0 && (
                  <div className="text-xs mt-1 flex items-center gap-1.5 flex-wrap" style={{ color: "var(--yalola-text-muted)" }} data-testid="app-campaign-by-lang">
                    <Languages size={12} /> {LANGS.filter(([k]) => status.by_lang?.[k]).map(([k, label]) => `${label} : ${status.by_lang[k]}`).join(" · ")}
                  </div>
                )}
                {status.last && <div className="text-xs mt-1" style={{ color: "var(--yalola-text-muted)" }}>Dernier envoi : {fmt(status.last.sent_at)} · {status.last.count} membre(s) · par {status.last.by_name || "admin"}</div>}
              </>
            ) : "Chargement…"}
          </div>
          {missingIos && (
            <div className="rounded-xl p-3 text-xs flex items-start gap-2" style={{ background: "#fff7ed", color: "#9a3412" }} data-testid="app-campaign-ios-warning">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <span>Le lien App Store n'est pas renseigné (Paramètres › PWA › URL App Store) : la page n'affichera que Google Play.</span>
            </div>
          )}
          <div className="flex gap-1.5 flex-wrap" data-testid="app-campaign-langs">
            {LANGS.map(([k, label]) => (
              <button key={k} type="button" onClick={() => setLang(k)} className="chip text-xs" style={lang === k ? { background: "var(--yalola-primary)", color: "#fff" } : undefined} data-testid={`app-campaign-lang-${k}`}>
                {label}{status?.by_lang?.[k] ? ` (${status.by_lang[k]})` : ""}
              </button>
            ))}
          </div>
          <div>
            <Label>Titre ({lang.toUpperCase()})</Label>
            <Input value={current.title || ""} onChange={(e) => setField("title", e.target.value)} maxLength={80} data-testid="app-campaign-title" />
          </div>
          <div>
            <Label>Message ({lang.toUpperCase()})</Label>
            <Textarea value={current.body || ""} onChange={(e) => setField("body", e.target.value)} rows={3} maxLength={220} data-testid="app-campaign-body" />
          </div>
          <a href="/get-app" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: "var(--yalola-primary)" }} data-testid="app-campaign-preview">Voir la page /get-app <ExternalLink size={12} /></a>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Annuler</Button>
          <Button className="pill-primary" onClick={send} disabled={busy || !status || !status.targets} data-testid="app-campaign-send">
            <Send size={14} className="mr-1" /> {busy ? "Envoi…" : `Envoyer à ${status?.targets ?? "…"} membre(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
